import React, { FC, ReactNode } from "react";
import { Form } from "react-final-form";
import { FieldInput } from "../../fields/FieldInput";
import { AlertTypes } from "../../alerts/Alert";
import { Button } from "../../Button";
import { Heading } from "../../Heading";
import { AuthPageContentLayout } from "../../layout/AuthPageContentLayout";
import {
  getSortedTermsByLocation,
  TermsAndConditions,
} from "../../terms/TermsAndConditions";
import { PasswordUpdateRequired } from "../../PasswordUpdateRequired";
import {
  CustomText,
  LocalizationQuery,
} from "../../../types/graphql/generated";
import { parseTemplateAsString } from "../../../helpers/localization";
import { validateWithYup, getLoginSchema } from "../../../helpers/validation";
import { formFields } from "../../../constants";
import {
  StyledAlert,
  StyledError,
  StyledForm,
  StyledHeading,
  StyledLoginButton,
  StyledLoginForm,
  StyledLogo,
  StyledResetPassword,
  StyledSignup,
} from "./index.styled";

interface LoginFormProps {
  alertDetails?: {
    alertType: AlertTypes;
    text: string;
  };
  canSignUp?: boolean;
  customTexts: CustomText[] | undefined;
  error: string | ReactNode;
  goToCreateAccount?: () => void;
  goToForgotPassword: () => void;
  goToShop?: () => void;
  inModal?: boolean;
  isLoading: boolean;
  localization?: LocalizationQuery["localization"] | null;
  logoSrc?: string;
  onSubmit: (formValues: LoginFormValues) => void;
  privacyUrl?: string;
  showUpdatePasswordScreen?: boolean;
  signinPageTitle?: string | null;
  tenantName: string | undefined;
}

export interface LoginFormValues {
  email: string;
  password: string;
}

export const LoginForm: FC<LoginFormProps> = ({
  alertDetails,
  canSignUp,
  customTexts = [],
  error,
  goToCreateAccount,
  goToForgotPassword,
  goToShop,
  inModal = false,
  isLoading,
  localization,
  logoSrc,
  onSubmit,
  showUpdatePasswordScreen = false,
  signinPageTitle,
  tenantName,
}) => {
  const { shopLogoAltText = "shop logo" } = localization?.portal?.alts || {};
  const {
    emailInputPlaceholderText = "Enter email address",
    passwordInputPlaceholderText = "Enter password",
    loginButtonText = "Login",
    forgotPasswordButtonText = "Forgot your password?",
  } = localization?.forms?.signIn || {};
  const {
    // eslint-disable-next-line no-template-curly-in-string
    signUpHeadingText = "New to ${tenantNameText}?",
    signUpDefaultHeadingText = "New here?",
    signUpButtonText = "Create an account",
  } = localization?.forms?.signIn || {};

  return (
    <AuthPageContentLayout inModal={inModal}>
      <StyledLoginForm>
        {!!logoSrc && <StyledLogo src={logoSrc} alt={shopLogoAltText} />}

        {alertDetails && (
          <StyledAlert bold closable type={alertDetails.alertType}>
            {alertDetails.text}
          </StyledAlert>
        )}

        {showUpdatePasswordScreen ? (
          <PasswordUpdateRequired
            goToShop={goToShop}
            localization={localization}
          />
        ) : (
          <>
            <Heading level="h2" size="l">
              {signinPageTitle}
            </Heading>
            <Form
              onSubmit={(formValues: LoginFormValues) => onSubmit(formValues)}
              validate={validateWithYup(
                getLoginSchema(localization?.forms?.validation)
              )}
              render={({ handleSubmit }) => (
                <StyledForm onSubmit={handleSubmit}>
                  <FieldInput
                    {...formFields.email}
                    placeholder={emailInputPlaceholderText}
                  />
                  <FieldInput
                    {...formFields.password}
                    type="password"
                    placeholder={passwordInputPlaceholderText}
                  />
                  <TermsAndConditions
                    terms={getSortedTermsByLocation({
                      location: "login",
                      customTexts: customTexts,
                    })}
                    type="info"
                  >
                    <StyledLoginButton>
                      <Button htmlType="submit" loading={isLoading} fullWidth>
                        {loginButtonText}
                      </Button>
                    </StyledLoginButton>
                    <StyledResetPassword>
                      <Button type="text" onClick={goToForgotPassword}>
                        {forgotPasswordButtonText}
                      </Button>
                    </StyledResetPassword>
                  </TermsAndConditions>
                  {error && <StyledError>{error}</StyledError>}
                </StyledForm>
              )}
            />
            {canSignUp && (
              <StyledSignup>
                <StyledHeading level="h3" size="m">
                  {tenantName
                    ? parseTemplateAsString(signUpHeadingText, {
                        tenantNameText: tenantName,
                      })
                    : signUpDefaultHeadingText}
                </StyledHeading>
                <Button
                  type="tertiary"
                  fullWidth
                  onClick={goToCreateAccount}
                  disabled={isLoading}
                >
                  {signUpButtonText}
                </Button>
              </StyledSignup>
            )}
          </>
        )}
      </StyledLoginForm>
    </AuthPageContentLayout>
  );
};
